import type { AdminUserListItemDto } from '@carcheck/shared';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';

import { initials, shortId } from './displayName';
import { RedactedField } from '../../../components/pii';
import { tokens } from '../../../design';
import { formatDate } from '../../../utils/format';
import { KycBadge, RoleBadge, StatusBadge } from '../_components/UserBadges';

/**
 * Column definitions for the users DataGrid (AUT-124), extracted from the
 * page so the page component stays under the line cap. Sorting is disabled
 * per column — the backend list is fixed `createdAt DESC` (no arbitrary
 * sort), so the grid never sends a sort the server can't honour.
 *
 * PII cells render through `<RedactedField perm="view_pii">`, which agrees
 * with `row.piiRevealed` (both derive from the same backend permission).
 * A revealed value is never re-redacted; only revealed values get a copy
 * button.
 */

/**
 * Identity cell: avatar (2-letter initials) + display name + truncated id.
 *
 * AUT-284 — the name used to render with its top half sliced off. Both
 * lines are explicitly BLOCK boxes with their own line-height: an inline
 * `<span>` (MUI's default element for `variant="caption"`) inherits the
 * DataGrid cell's row-height strut, which inflated this two-line stack past
 * the 52px row and let `overflow: hidden` clip it. `AdminDataGrid` also
 * flexes the cell now — belt and braces, because the shape of this cell is
 * what the operator reads first.
 */
const UserCell = ({ row, t }: { row: AdminUserListItemDto; t: TFunction }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, width: '100%' }}>
    <Avatar sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
      {initials(row.displayName)}
    </Avatar>
    <Box sx={{ minWidth: 0, lineHeight: 'normal' }}>
      <Typography component="div" variant="body2" fontWeight={600} noWrap>
        {row.displayName ?? t('users.detail.noValue')}
      </Typography>
      <Typography
        component="div"
        variant="caption"
        noWrap
        sx={{ color: tokens.text.tertiary }}
        fontFamily="monospace"
      >
        {shortId(row.id)}
      </Typography>
    </Box>
  </Box>
);

const PiiCell = ({ value, revealed }: { value: string; revealed: boolean }): ReactElement =>
  revealed ? (
    <RedactedField perm="view_pii" value={value} redacted={value} copyable />
  ) : (
    <RedactedField perm="view_pii" value={value} redacted={value} />
  );

export const buildUserColumns = (t: TFunction): GridColDef<AdminUserListItemDto>[] => [
  {
    field: 'user',
    headerName: t('users.columns.user'),
    flex: 1.4,
    minWidth: 220,
    sortable: false,
    renderCell: ({ row }) => <UserCell row={row} t={t} />,
  },
  {
    field: 'phoneNumber',
    headerName: t('users.columns.phone'),
    flex: 1,
    minWidth: 160,
    sortable: false,
    renderCell: ({ row }) => <PiiCell value={row.phoneNumber} revealed={row.piiRevealed} />,
  },
  {
    field: 'email',
    headerName: t('users.columns.email'),
    flex: 1.2,
    minWidth: 180,
    sortable: false,
    renderCell: ({ row }) => {
      const email = row.email ?? '';
      if (!email) return <Typography variant="body2">{t('users.detail.noEmail')}</Typography>;
      return <PiiCell value={email} revealed={row.piiRevealed} />;
    },
  },
  {
    field: 'role',
    headerName: t('users.columns.role'),
    flex: 0.6,
    minWidth: 110,
    sortable: false,
    renderCell: ({ row }) => <RoleBadge role={row.role} />,
  },
  {
    field: 'status',
    headerName: t('users.columns.status'),
    flex: 0.7,
    minWidth: 120,
    sortable: false,
    renderCell: ({ row }) => <StatusBadge status={row.status} />,
  },
  {
    field: 'kycStatus',
    headerName: t('users.columns.kyc'),
    flex: 0.8,
    minWidth: 120,
    sortable: false,
    renderCell: ({ row }) => <KycBadge kycStatus={row.kycStatus} />,
  },
  {
    field: 'createdAt',
    headerName: t('users.columns.registered'),
    flex: 0.7,
    minWidth: 120,
    sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2" color="text.secondary" fontFamily="monospace">
        {formatDate(row.createdAt)}
      </Typography>
    ),
  },
];
